/**
 * Generates a user-friendly display name from an email address
 * This helps protect user privacy by not showing full email addresses publicly
 * @param {string} email - The email address to generate a display name from
 * @param {string} customDisplayName - Optional custom display name from user profile
 * @returns {string} - A user-friendly display name
 */
export function generateDisplayName(email, customDisplayName = null) {
  // If user has set a custom display name, use that
  if (customDisplayName && typeof customDisplayName === 'string' && customDisplayName.trim()) {
    return customDisplayName.trim();
  }

  if (!email || typeof email !== 'string') {
    return 'Anonymous User';
  }
  
  try {
    // Extract the username part before the @
    const username = email.split('@')[0];
    
    if (!username) {
      return 'Anonymous User';
    }
    
    // Handle common username patterns and clean them up
    const cleanName = username
      // Replace dots, underscores, hyphens with spaces
      .replace(/[._-]/g, ' ')
      // Remove numbers from the end (like john.doe123 becomes john doe)
      .replace(/\d+$/, '')
      // Split into words and capitalize each word
      .split(' ')
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .filter(word => word.length > 0) // Remove empty words
      .join(' ');
    
    // If we end up with an empty string after cleaning, fall back to a simple version
    if (!cleanName || cleanName.trim().length === 0) {
      const simpleUsername = username.replace(/[^a-zA-Z]/g, '');
      return simpleUsername ? 
        simpleUsername.charAt(0).toUpperCase() + simpleUsername.slice(1).toLowerCase() :
        'User';
    }
    
    return cleanName.trim();
  } catch (error) {
    console.error('Error generating display name:', error);
    return 'User';
  }
}

/**
 * Gets the final display name that should be shown to users
 * Prioritizes custom display name, falls back to email-based generation
 * @param {string} userEmail - The user's email address  
 * @param {string} userDisplayName - The user's custom display name (if any)
 * @returns {string} - The display name to show
 */
export function getFinalDisplayName(userEmail, userDisplayName) {
  return generateDisplayName(userEmail, userDisplayName);
}

/**
 * Gets initials from a display name
 * @param {string} displayName - The display name to generate initials from
 * @returns {string} - User initials (max 2 characters)
 */
export function getInitials(displayName) {
  if (!displayName || typeof displayName !== 'string') {
    return 'AU'; // Anonymous User
  }
  
  try {
    const name = displayName.trim();
    
    if (!name) {
      return 'U';
    }
    
    const words = name.split(' ').filter(word => word.length > 0);
    
    if (words.length === 0) {
      return 'U';
    } else if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    } else {
      return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    }
  } catch (error) {
    console.error('Error generating initials:', error);
    return 'U';
  }
}

